import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Moon, Briefcase, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, StatusBadge } from "../../components/Common";
import { DAYS, CHIP, buildCells, shiftMonth } from "../admin/AdminSchedule";
import { api, currentMonth, dateLabel, formatApiError, monthLabel, todayStr } from "../../lib/api";

const group = (list, key) => list.reduce((m, x) => ({ ...m, [x[key]]: [...(m[x[key]] || []), x] }), {});

function weekDates() {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  });
}

function WeekStrip({ byDay, offByDay, onSelect, selected }) {
  return (
    <section className="mb-8" data-testid="week-strip">
      <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">Minggu Ini</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {weekDates().map((d) => {
          const trips = byDay[d] || [];
          const off = offByDay[d];
          return (
            <button key={d} onClick={() => onSelect(d)} className={`rise rounded-xl border p-3 text-left min-h-[88px] transition-colors ${selected === d ? "border-emerald-500 ring-2 ring-emerald-200" : "border-slate-200"} ${off ? "bg-slate-100" : "bg-white hover:border-slate-300"}`} data-testid={`week-day-${d}`}>
              <p className={`text-xs font-bold ${d === todayStr() ? "text-emerald-700" : "text-slate-700"}`}>{dateLabel(d)}</p>
              {off && <p className="mt-1 text-xs font-semibold text-slate-500">Libur / Off</p>}
              {trips.map((t) => <p key={t.id} className={`mt-1 truncate rounded px-1.5 py-0.5 text-[11px] font-semibold ${CHIP[t.status]}`}>{t.tour_name}</p>)}
              {!off && !trips.length && <p className="mt-1 text-[11px] text-slate-400">Kosong</p>}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function OffControl({ date, trips, off, onChanged }) {
  const [note, setNote] = useState("");
  const [end, setEnd] = useState(date);
  const [saving, setSaving] = useState(false);
  useEffect(() => setEnd(date), [date]);
  const past = date < todayStr();
  const run = async (fn, msg) => {
    setSaving(true);
    try { await fn(); toast.success(msg); setNote(""); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  if (past) return <p className="text-xs text-slate-400">Tanggal sudah lewat.</p>;
  if (off)
    return (
      <Button onClick={() => run(() => api.delete(`/dayoffs/${off.id}`), "Status kembali: Kerja")} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="set-work-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Briefcase className="h-4 w-4 mr-2" />} Saya Kerja di Tanggal Ini
      </Button>
    );
  if (trips.length) return <p className="text-xs text-slate-500">Sudah ada trip/order di tanggal ini. Hubungi admin jika ingin libur.</p>;
  const saveOff = () => run(async () => {
    const { data } = await api.post("/dayoffs/range", { start: date, end: end < date ? date : end, note });
    if (data.skipped.length) toast.warning(`${data.skipped.length} tanggal dilewati (${data.skipped.map((s) => `${Number(s.date.slice(8))}: ${s.reason}`).join(", ")})`);
    if (!data.created.length) throw new Error("Tidak ada tanggal yang bisa ditandai libur");
  }, "Libur / Off tersimpan");
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="min-w-0 space-y-1"><span className="text-[11px] font-semibold text-slate-500">Dari</span><Input type="date" className="min-w-0 px-2 text-xs" value={date} disabled data-testid="off-start-input" /></label>
        <label className="min-w-0 space-y-1"><span className="text-[11px] font-semibold text-slate-500">Sampai</span><Input type="date" className="min-w-0 px-2 text-xs" min={date} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="off-end-input" /></label>
      </div>
      <Input placeholder="Alasan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="off-note-input" />
      <Button onClick={saveOff} disabled={saving} variant="outline" className="w-full" data-testid="set-off-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Moon className="h-4 w-4 mr-2" />} {end > date ? "Tandai Libur Beberapa Hari" : "Tandai Libur / Off"}
      </Button>
    </div>
  );
}

function DayPanel({ date, trips, off, onChanged }) {
  return (
    <aside className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 lg:sticky lg:top-6" data-testid="driver-day-panel">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-600">{off ? "Libur / Off" : "Kerja"}</p>
        <h2 className="font-heading text-xl font-bold text-slate-900" data-testid="driver-day-date">{dateLabel(date)}</h2>
        {off?.note && <p className="text-xs text-slate-500">{off.note}</p>}
      </div>
      {trips.map((t) => (
        <div key={t.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={`driver-day-trip-${t.id}`}>
          <div className="flex items-center justify-between gap-2"><p className="font-semibold text-sm">{t.tour_name}</p><StatusBadge status={t.status} /></div>
          {t.driver_note && <p className="mt-1 text-xs text-slate-600 whitespace-pre-line line-clamp-6">{t.driver_note}</p>}
        </div>
      ))}
      {!trips.length && !off && <p className="text-sm text-slate-400">Belum ada trip.</p>}
      <OffControl date={date} trips={trips} off={off} onChanged={onChanged} />
    </aside>
  );
}

export default function DriverSchedule() {
  const [month, setMonth] = useState(currentMonth());
  const [selected, setSelected] = useState(todayStr());
  const [trips, setTrips] = useState([]);
  const [offs, setOffs] = useState([]);

  const load = useCallback(() => Promise.all([api.get("/trips"), api.get("/dayoffs")]).then(([t, o]) => { setTrips(t.data); setOffs(o.data); }), []);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => group(trips, "trip_date"), [trips]);
  const offByDay = useMemo(() => Object.fromEntries(offs.map((o) => [o.date, o])), [offs]);
  const select = (d) => { setSelected(d); setMonth(d.slice(0, 7)); };
  const changeMonth = (n) => { const m = shiftMonth(month, n); setMonth(m); setSelected(`${m}-01`); };

  return (
    <div>
      <PageHeader eyebrow="Jadwal Saya" title={monthLabel(month)}>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="driver-cal-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => select(todayStr())} data-testid="driver-cal-today-btn">Hari ini</Button>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="driver-cal-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </PageHeader>
      <WeekStrip byDay={byDay} offByDay={offByDay} onSelect={select} selected={selected} />
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div data-testid="driver-calendar">
          <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1">{DAYS.map((d) => <p key={d} className="text-center text-[11px] font-semibold uppercase text-slate-400">{d}</p>)}</div>
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {buildCells(month).map((d, i) => d ? (
              <button key={d} onClick={() => setSelected(d)} className={`min-h-[56px] sm:min-h-[90px] rounded-xl border p-1.5 sm:p-2 text-left ${d === selected ? "border-emerald-500 ring-2 ring-emerald-200" : "border-slate-200"} ${offByDay[d] ? "bg-slate-100" : "bg-white"}`} data-testid={`driver-cal-day-${d}`}>
                <span className={`text-xs font-bold ${d === todayStr() ? "rounded-full bg-slate-900 text-white px-1.5" : "text-slate-700"}`}>{Number(d.slice(8))}</span>
                {offByDay[d] && <p className="mt-1 text-[10px] font-semibold text-slate-500" data-testid={`driver-cal-off-${d}`}>Libur</p>}
                {(byDay[d] || []).slice(0, 2).map((t) => <p key={t.id} className={`hidden sm:block mt-1 truncate rounded px-1.5 py-0.5 text-[10px] font-semibold ${CHIP[t.status]}`}>{t.tour_name}</p>)}
                {(byDay[d] || []).length > 0 && <p className="sm:hidden mt-1 text-[10px] font-bold text-emerald-700">{byDay[d].length} trip</p>}
              </button>
            ) : <div key={`e${i}`} />)}
          </div>
        </div>
        <DayPanel date={selected} trips={byDay[selected] || []} off={offByDay[selected]} onChanged={load} />
      </div>
    </div>
  );
}
